import type { CategorieDossier } from '@repere/core';

/**
 * Pictogrammes Repère — le langage graphique de la marque, à la taille de l'interface.
 *
 * Trois règles, reprises de la planche d'icônes (`packages/ui-tokens` → `iconPalette`) :
 * des **formes pleines sans contour**, **deux teintes** par dessin, et le détail
 * **creusé en négatif** plutôt que posé par-dessus. Le trait fin est le langage des outils
 * de productivité ; la surface pleine et arrondie est celui de l'enfance, et c'est le
 * second ton qu'il faut à un produit qui parle des enfants de gens en séparation.
 *
 * **Le négatif n'est pas un effet de style, c'est ce qui tient à 20 px.** Les aiguilles
 * d'une horloge posées en vert sur un cadran bleu se rejoindraient en une bouillie dès que
 * l'icône rétrécit — les deux teintes du langage ont presque la même luminance. Creusées,
 * elles laissent passer le fond, quel qu'il soit, et gardent tout leur contraste.
 * D'où le `fillRule="evenodd"` : les formes intérieures percent la forme principale.
 *
 * Les couleurs viennent du contexte (`--picto-primaire`, `--picto-secondaire`) : un onglet
 * actif fonce les deux teintes, un bloc vide les passe en pastel. Le dessin ne change pas.
 */
const TRACES: Record<CategorieDossier, { primaire: string; secondaire: string }> = {
  /** cadran plein, aiguilles creusées, point satellite */
  aujourdhui: {
    primaire:
      'M3.00 12.60 a8.40 8.40 0 1 0 16.80 0 a8.40 8.40 0 1 0 -16.80 0 Z M10.45 7.75 Q10.45 6.90 11.30 6.90 L11.50 6.90 Q12.35 6.90 12.35 7.75 L12.35 10.80 Q12.35 11.65 13.20 11.65 L15.05 11.65 Q15.90 11.65 15.90 12.50 L15.90 12.70 Q15.90 13.55 15.05 13.55 L11.30 13.55 Q10.45 13.55 10.45 12.70 Z',
    secondaire:
      'M17.10 4.60 a2.50 2.50 0 1 0 5.00 0 a2.50 2.50 0 1 0 -5.00 0 Z',
  },
  /** bloc plein, jours creusés, deux attaches en tête */
  calendrier: {
    primaire:
      'M3.00 10.60 Q3.00 6.20 7.40 6.20 L16.40 6.20 Q20.80 6.20 20.80 10.60 L20.80 16.40 Q20.80 20.80 16.40 20.80 L7.40 20.80 Q3.00 20.80 3.00 16.40 Z M6.90 14.60 a1.30 1.30 0 1 0 2.60 0 a1.30 1.30 0 1 0 -2.60 0 Z M10.60 14.60 a1.30 1.30 0 1 0 2.60 0 a1.30 1.30 0 1 0 -2.60 0 Z M14.30 14.60 a1.30 1.30 0 1 0 2.60 0 a1.30 1.30 0 1 0 -2.60 0 Z',
    secondaire:
      'M7.00 3.80 Q7.00 2.40 8.40 2.40 L8.40 2.40 Q9.80 2.40 9.80 3.80 L9.80 6.00 Q9.80 7.40 8.40 7.40 L8.40 7.40 Q7.00 7.40 7.00 6.00 Z M14.00 3.80 Q14.00 2.40 15.40 2.40 L15.40 2.40 Q16.80 2.40 16.80 3.80 L16.80 6.00 Q16.80 7.40 15.40 7.40 L15.40 7.40 Q14.00 7.40 14.00 6.00 Z',
  },
  /** bulle à queue, deux points creusés, écho vert */
  messages: {
    primaire:
      'M3.20 7.30 Q3.20 4.60 5.90 4.60 L15.70 4.60 Q18.40 4.60 18.40 7.30 L18.40 12.70 Q18.40 15.40 15.70 15.40 L12.30 15.40 Q9.60 15.40 7.84 17.45 L7.76 17.55 Q6.00 19.60 6.20 17.50 L6.20 17.50 Q6.40 15.40 4.80 15.40 L4.80 15.40 Q3.20 15.40 3.20 12.70 Z M7.15 10.00 a1.25 1.25 0 1 0 2.50 0 a1.25 1.25 0 1 0 -2.50 0 Z M11.95 10.00 a1.25 1.25 0 1 0 2.50 0 a1.25 1.25 0 1 0 -2.50 0 Z',
    secondaire:
      'M16.70 17.80 a2.90 2.90 0 1 0 5.80 0 a2.90 2.90 0 1 0 -5.80 0 Z',
  },
  /** carte pleine, fente creusée, pièce en débord */
  depenses: {
    primaire:
      'M2.40 9.90 Q2.40 6.40 5.90 6.40 L13.50 6.40 Q17.00 6.40 17.00 9.90 L17.00 14.30 Q17.00 17.80 13.50 17.80 L5.90 17.80 Q2.40 17.80 2.40 14.30 Z M5.00 10.35 Q5.00 9.40 5.95 9.40 L13.45 9.40 Q14.40 9.40 14.40 10.35 L14.40 10.35 Q14.40 11.30 13.45 11.30 L5.95 11.30 Q5.00 11.30 5.00 10.35 Z',
    secondaire:
      'M13.90 14.60 a4.30 4.30 0 1 0 8.60 0 a4.30 4.30 0 1 0 -8.60 0 Z',
  },
  /** page pleine, lignes creusées, signet vert */
  journal: {
    primaire:
      'M4.00 6.50 Q4.00 3.00 7.50 3.00 L13.90 3.00 Q17.40 3.00 17.40 6.50 L17.40 17.50 Q17.40 21.00 13.90 21.00 L7.50 21.00 Q4.00 21.00 4.00 17.50 Z M7.00 8.70 Q7.00 7.80 7.90 7.80 L13.50 7.80 Q14.40 7.80 14.40 8.70 L14.40 8.70 Q14.40 9.60 13.50 9.60 L7.90 9.60 Q7.00 9.60 7.00 8.70 Z M7.00 12.30 Q7.00 11.40 7.90 11.40 L13.50 11.40 Q14.40 11.40 14.40 12.30 L14.40 12.30 Q14.40 13.20 13.50 13.20 L7.90 13.20 Q7.00 13.20 7.00 12.30 Z M7.00 15.90 Q7.00 15.00 7.90 15.00 L10.70 15.00 Q11.60 15.00 11.60 15.90 L11.60 15.90 Q11.60 16.80 10.70 16.80 L7.90 16.80 Q7.00 16.80 7.00 15.90 Z',
    secondaire:
      'M16.60 6.70 Q16.60 5.20 18.10 5.20 L18.90 5.20 Q20.40 5.20 20.40 6.70 L20.40 14.10 Q20.40 15.60 19.45 14.60 L19.45 14.60 Q18.50 13.60 17.55 14.60 L17.55 14.60 Q16.60 15.60 16.60 14.10 Z',
  },
  /** silhouette bras écartés : tête verte, corps bleu */
  enfants: {
    primaire:
      'M10.35 8.90 Q11.90 8.20 13.45 8.90 L13.45 8.90 Q15.00 9.60 16.61 10.16 L18.29 10.74 Q19.90 11.30 20.35 12.80 L20.35 12.80 Q20.80 14.30 19.12 14.58 L18.88 14.62 Q17.20 14.90 16.30 14.25 L16.30 14.25 Q15.40 13.60 15.40 15.05 L15.40 15.05 Q15.40 16.50 15.91 18.12 L16.29 19.38 Q16.80 21.00 15.25 21.40 L15.25 21.40 Q13.70 21.80 12.99 20.26 L12.61 19.44 Q11.90 17.90 11.19 19.44 L10.81 20.26 Q10.10 21.80 8.55 21.40 L8.55 21.40 Q7.00 21.00 7.51 19.38 L7.89 18.12 Q8.40 16.50 8.40 15.05 L8.40 15.05 Q8.40 13.60 7.50 14.25 L7.50 14.25 Q6.60 14.90 4.92 14.62 L4.68 14.58 Q3.00 14.30 3.45 12.80 L3.45 12.80 Q3.90 11.30 5.51 10.74 L7.19 10.16 Q8.80 9.60 10.35 8.90 Z',
    secondaire:
      'M8.40 5.00 a3.50 3.50 0 1 0 7.00 0 a3.50 3.50 0 1 0 -7.00 0 Z',
  },
};

/**
 * Un pictogramme de catégorie.
 *
 * `aria-hidden` sans exception : chaque pictogramme accompagne toujours son libellé écrit,
 * et l'annoncer une seconde fois au lecteur d'écran ferait entendre « Messages Messages ».
 */
export function Picto({
  categorie,
  taille = 20,
}: {
  categorie: CategorieDossier;
  taille?: number;
}) {
  const trace = TRACES[categorie];
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="picto"
    >
      <path d={trace.primaire} fillRule="evenodd" fill="var(--picto-primaire, #3D5573)" />
      <path d={trace.secondaire} fill="var(--picto-secondaire, #6E8F7C)" />
    </svg>
  );
}
